import { isAutomaticMelding, normalizeText } from './analyticsShared';
import { IDENTITY_STATUS, resolveRobotRecord } from './identityResolver';

export const ANALYTICS_MANUAL_COLLECTION = 'meldingen';

const comparable = (value) => normalizeText(value).replace(/[^a-z0-9]+/g, ' ').trim();
const tokenKey = (value) => comparable(value).split(' ').filter(Boolean).sort().join(' ');
const same = (left, right) => Boolean(comparable(left)) && comparable(left) === comparable(right);
const get = (object, path) => path.split('.').reduce((value, key) => value == null ? undefined : value[key], object);

export function normalizeRobotName(value) {
  return comparable(value);
}

export function getAnalyticsCollectionPath(branchId) {
  return ['vestigingen', branchId, ANALYTICS_MANUAL_COLLECTION];
}

export function extractMeldingRobotIdentifiers(melding = {}) {
  const names = [melding.naam, melding.name, melding.robotName, melding.robotNaam,
    typeof melding.robot === 'string' ? melding.robot : '', get(melding, 'selectedRobot.value'),
    get(melding, 'selectedRobot.label'), get(melding, 'selectedRobot.robot.naam'), get(melding, 'selectedRobot.robot.name')]
    .filter(Boolean);
  const explicitIds = [get(melding, 'selectedRobot.robot.id'), melding.robotDocumentId, melding.robotDocId,
    melding.documentId, melding.robotId, melding.robotID, get(melding, 'selectedRobot.id'),
    typeof melding.robot === 'string' ? melding.robot : '', melding.id]
    .filter(Boolean);
  const serials = [melding.belroboticsId, melding.serial, melding.serialNumber, melding.robotSerial,
    get(melding, 'selectedRobot.robot.belroboticsId'), get(melding, 'selectedRobot.robot.serialNumber')].filter(Boolean);
  return {
    explicitIds,
    serials,
    names,
    kNummer: melding.kNummer ?? get(melding, 'selectedRobot.robot.kNummer') ?? '',
    projectNummer: melding.projectNummer ?? get(melding, 'selectedRobot.robot.projectNummer') ?? '',
    gemeente: melding.gemeente ?? get(melding, 'selectedRobot.robot.gemeente') ?? '',
  };
}

function uniqueMatch(robots, predicate) {
  const matches = robots.filter(predicate);
  return matches.length === 1 ? matches[0] : null;
}

export function linkMeldingToCurrentRobot(melding = {}, robots = [], { strictStableIdentity = false } = {}) {
  if (strictStableIdentity) {
    const resolution = resolveRobotRecord(melding, robots);
    if (resolution.status !== IDENTITY_STATUS.PROVEN) {
      return { robot: null, rule: resolution.reason || 'MANUAL_REVIEW', confidence: 'UNRESOLVED' };
    }
    return { robot: resolution.robot, rule: 'EXACT_STABLE_IDENTIFIER', confidence: 'HIGH' };
  }
  const identifiers = extractMeldingRobotIdentifiers(melding);
  const robotIds = new Map(robots.flatMap(robot => [robot.docId, robot.id].filter(Boolean).map(id => [comparable(id), robot])));
  const explicitFields = [get(melding, 'selectedRobot.robot.id'), melding.robotDocumentId, melding.robotDocId,
    melding.documentId, melding.robotId, melding.robotID, get(melding, 'selectedRobot.id'),
    typeof melding.robot === 'string' ? melding.robot : ''].filter(Boolean);
  for (const explicitId of explicitFields) {
    const robot = robotIds.get(comparable(explicitId));
    if (robot) return { robot, rule: 'EXACT_ROBOT_DOCUMENT_ID', confidence: 'HIGH' };
  }

  const serialRobot = uniqueMatch(robots, robot => identifiers.serials.some(serial =>
    [robot.belroboticsId, robot.serial, robot.serialNumber, robot.robotSerial, robot.docId, robot.id]
      .some(value => same(serial, value))));
  if (serialRobot) return { robot: serialRobot, rule: 'EXACT_UNIQUE_SERIAL', confidence: 'HIGH' };

  const kMatches = robots.filter(robot => same(robot.kNummer, identifiers.kNummer));
  if (kMatches.length === 1) return { robot: kMatches[0], rule: 'EXACT_UNIQUE_KNUMMER', confidence: 'HIGH' };
  if (kMatches.length > 1) {
    const byProject = uniqueMatch(kMatches, robot => same(robot.projectNummer, identifiers.projectNummer));
    if (byProject) return { robot: byProject, rule: 'KNUMMER_PROJECTNUMMER', confidence: 'HIGH' };
    const byGemeente = uniqueMatch(kMatches, robot => same(robot.gemeente, identifiers.gemeente));
    if (byGemeente) return { robot: byGemeente, rule: 'KNUMMER_GEMEENTE', confidence: 'HIGH' };
  }

  const projectName = uniqueMatch(robots, robot => same(robot.projectNummer, identifiers.projectNummer)
    && identifiers.names.some(name => [robot.docId, robot.id, robot.naam, robot.name].some(robotName => tokenKey(name) === tokenKey(robotName))));
  if (projectName) return { robot: projectName, rule: 'PROJECTNUMMER_NAME', confidence: 'HIGH' };

  const uniqueName = uniqueMatch(robots, robot => identifiers.names.some(name =>
    [robot.docId, robot.id, robot.naam, robot.name].some(robotName => tokenKey(name) === tokenKey(robotName))));
  if (uniqueName) return { robot: uniqueName, rule: 'EXACT_UNIQUE_NAME', confidence: 'HIGH' };

  // Historical Heerlen records often store the project number in kNummer and an old display name.
  const number = identifiers.names.map(name => comparable(name).match(/\b\d{3,4}\b/)?.[0]).find(Boolean);
  if (number) {
    const historical = uniqueMatch(robots, robot => comparable(robot.docId).startsWith(`${number} `)
      && (same(robot.projectNummer, identifiers.projectNummer) || same(robot.projectNummer, identifiers.kNummer))
      && same(robot.gemeente, identifiers.gemeente));
    if (historical) return { robot: historical, rule: 'HISTORICAL_NUMBER_PROJECT_GEMEENTE', confidence: 'HIGH' };
  }

  const historicalName = uniqueMatch(robots, robot => identifiers.names.some(name =>
    [robot.docId, robot.id, robot.naam, robot.name].some(robotName => comparable(robotName).replace(/^\d+ /, '') === comparable(name).replace(/^\d+ /, '').replace(/ \d+$/, '')))
    && (same(robot.projectNummer, identifiers.projectNummer) || same(robot.projectNummer, identifiers.kNummer))
    && same(robot.gemeente, identifiers.gemeente));
  if (historicalName) return { robot: historicalName, rule: 'HISTORICAL_NAME_PROJECT_GEMEENTE', confidence: 'HIGH' };

  return { robot: null, rule: 'MANUAL_REVIEW', confidence: 'UNRESOLVED' };
}

export function prepareAnalyticsMeldingen(meldingen = [], robots = [], { includeAutomatic = false, recordType = null, strictStableIdentity = false } = {}) {
  const unique = new Map();
  meldingen.forEach(melding => {
    if (!melding || melding.placeholder || melding.deleted === true || (!includeAutomatic && isAutomaticMelding(melding))) return;
    const id = String(melding.id || melding._id || '').trim();
    if (!id || unique.has(id)) return;
    unique.set(id, melding);
  });

  const linked = [];
  const manualReview = [];
  unique.forEach(melding => {
    const result = linkMeldingToCurrentRobot(melding, robots, { strictStableIdentity });
    if (!result.robot) {
      manualReview.push({ melding, meldingId: melding.id || melding._id, rule: result.rule });
      return;
    }
    linked.push({
      ...melding,
      robotDocumentId: result.robot.docId,
      robotLinkRule: result.rule,
      robotLinkConfidence: result.confidence,
      serialNumber: result.robot.serialNumber || result.robot.serial || result.robot.robotSerial || result.robot.belroboticsId || '',
      naam: result.robot.naam || result.robot.name || result.robot.docId,
      robotName: result.robot.naam || result.robot.name || result.robot.docId,
      gemeente: result.robot.gemeente || '',
      kNummer: result.robot.kNummer || '',
      projectNummer: result.robot.projectNummer || '',
      analyticsRecordType: recordType || (isAutomaticMelding(melding) ? 'automatic_incident' : 'manual_visit'),
      automatic: isAutomaticMelding(melding),
    });
  });
  return { sourceRecords: unique.size, linked, manualReview };
}

export function aggregateAnalyticsByRobot(records = [], robots = []) {
  const rows = new Map(robots.map(robot => [robot.docId, {
    robotDocumentId: robot.docId, robotName: robot.naam || robot.name || robot.docId,
    gemeente: robot.gemeente || '', kNummer: robot.kNummer || '', projectNummer: robot.projectNummer || '',
    meldingen: 0, uren: 0, mesjes: 0, bijzonderheden: 0, technici: new Set(),
  }]));
  records.forEach(record => {
    const row = rows.get(record.robotDocumentId);
    if (!row) return;
    row.meldingen += 1;
    if (record.analyticsRecordType === 'manual_visit') {
      row.uren += Number(record.uren ?? record.hours ?? 0) || 0;
      row.mesjes += Number(record.mesjes ?? 0) || 0;
      if (String(record.bijzonderheden || record.details || record.opmerkingen || '').trim()) row.bijzonderheden += 1;
      const technician = String(record.displayName || record.technician || record.username || record.email || '').trim();
      if (technician) row.technici.add(technician);
    }
  });
  return [...rows.values()].map(row => ({ ...row, technici: [...row.technici] }));
}
